import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, ButtonType } from '../../../../../shared/components/button/button.component';
import { PmsType, formatPmsType } from '../../../types/pms';

const PMS_OPTIONS: { type: PmsType; available: boolean }[] = [
    { type: PmsType.Mews, available: true },
    { type: PmsType.Apaleo, available: false },
    { type: PmsType.OperaCloud, available: true },
    { type: PmsType.Guestline, available: true },
    { type: PmsType.Shiji, available: true },
    { type: PmsType.HotelKey, available: false },
    { type: PmsType.StayNTouch, available: true },
];

@Component({
    selector: 'juyo-select-pms-step',
    styleUrl: './select-pms-step.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ButtonComponent],
    template: `
        <div class="step-body">
            <h3 class="step-title">Choose your property management system</h3>
            <p class="step-subtitle">We'll use it to pull reservation and performance data.</p>

            <div class="search">
                <input type="text" placeholder="Search PMS..." class="search-input"
                       [ngModel]="pmsSearch()" (ngModelChange)="onPmsSearch($event)" />
            </div>

            <div class="pms-grid">
                @for (pms of filteredPms(); track pms.type) {
                    <div class="pms-card"
                         [class.available]="pms.available"
                         [class.selected]="selectedPmsType() === pms.type"
                         [class.disabled]="!pms.available"
                         (click)="onSelectPms(pms)">
                        <span class="pms-name">{{ formatPmsType(pms.type) }}</span>
                        <span class="pms-type">API Integration</span>
                        @if (pms.available) {
                            <span class="badge badge-available">AVAILABLE</span>
                        } @else {
                            <span class="badge badge-coming-soon">COMING SOON</span>
                        }
                    </div>
                }
            </div>

            <p class="help-text">
                Don't see your PMS, or having trouble adding your property?
                <a href="mailto:onboarding@juyoanalytics.com" class="link">Let us know</a>.
            </p>
        </div>

        <div class="step-footer">
            @if (showBack()) {
                <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="back.emit()">
                    <span class="material-symbols-outlined">arrow_left_alt</span> Back
                </juyo-button>
            } @else {
                <div></div>
            }
            <div class="footer-actions">
                @if (showSkip()) {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="skip.emit()">
                        Skip for now
                    </juyo-button>
                } @else {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="cancel.emit()">
                        Cancel
                    </juyo-button>
                }
                <juyo-button [disabled]="!selectedPmsType()" (clicked)="onContinue()">
                    Continue <span class="material-symbols-outlined">arrow_right_alt</span>
                </juyo-button>
            </div>
        </div>
    `,
})
export class SelectPmsStepComponent {
    public showBack = input<boolean>(true);
    public showSkip = input<boolean>(true);

    public continue = output<PmsType>();
    public back = output<void>();
    public cancel = output<void>();
    public skip = output<void>();

    protected readonly ButtonType = ButtonType;
    protected readonly formatPmsType = formatPmsType;

    protected pmsSearch = signal('');
    protected selectedPmsType = signal<PmsType | null>(null);

    protected filteredPms = computed(() => {
        const q = this.pmsSearch().toLowerCase();
        return q ? PMS_OPTIONS.filter((p) => formatPmsType(p.type).toLowerCase().includes(q)) : PMS_OPTIONS;
    });

    protected onSelectPms(pms: { type: PmsType; available: boolean }): void {
        if (pms.available) {
            this.selectedPmsType.set(pms.type);
            this.continue.emit(pms.type);
        }
    }

    protected onPmsSearch(value: string): void {
        this.pmsSearch.set(value);
        this.selectedPmsType.set(null);
    }

    protected onContinue(): void {
        const type = this.selectedPmsType();
        if (type) {
            this.continue.emit(type);
        }
    }
}
